import t from '../lib/I18n';
import _ from 'lodash';
import moment from 'moment';
import { List } from 'immutable';
import trainTime from '../lib/TrainTime';


export const stationDisplayName = station =>
    station.crs
        ? t('booking.journey.stationName', _.pick(station.toJS(), ['name', 'crs']))
        : station.name;

export const buildSalesReservationPayload = ({
    outwardService,
    inwardService,
    outwardFare,
    inwardFare,
    passengers = List(),
    bikesCount = 0,
    requestSeats = true,
}) => {
    const buildLegs = (service, direction) => {
        const legs = [];
        const serviceLegs = service && service.get('legs');
    
        if (serviceLegs && serviceLegs.size > 0) {
            for (let i = 0; i < serviceLegs.size; i++) {
                const leg = serviceLegs.get(i);
    
                const mode = leg.getIn(['serviceDetails', 'mode']);
                if (mode !== 'Train') {
                    continue;  // Skip non-train legs
                }
    
                const origin = Array.isArray(leg.origin)
                    ? leg.origin[0]
                    : (leg.get('origin') && leg.get('origin').get(0)) || {};
                const destination = Array.isArray(leg.destination)
                    ? leg.destination[0]
                    : (leg.get('destination') && leg.get('destination').get(0)) || {};
    
                const originTime =
                    origin &&
                    origin.time &&
                    origin.time.scheduledTime
                        ? origin.time.scheduledTime
                        : '';
                const formattedDate = originTime
                    ? moment(originTime).format('YYYY-MM-DD')
                    : '';
    
                const boardLocation = origin &&
                    ((origin.station && origin.station.crs) ||
                        (origin.getIn && origin.getIn(['station', 'crs']))) || '';
    
                const alightLocation = destination &&
                    ((destination.station && destination.station.crs) ||
                        (destination.getIn && destination.getIn(['station', 'crs']))) || '';
    
                const rsid = leg.getIn(['serviceDetails', 'retailServiceId']) || '';
    
                legs.push({
                    id: `segment_${i + 1}_${direction}`,
                    boardLocation,
                    alightLocation,
                    rsid,
                    serviceOriginDate: formattedDate,
                });
            }
        } else {
            console.warn('Invalid or missing service legs.');
        }
    
        return legs;
    };
    

    const buildBundles = ({
        direction,
        ticketTypeCode,
        routeCode,
        passengerIds,
        bikesCount,
        requestSeats,
    }) => {
        const bundles = [];

        if (requestSeats && ticketTypeCode) {
            const seatProducts = passengerIds.map((id, index) => ({
                ticketTypeCode,
                routeCode,
                sequenceNumber: index + 1,
                passengerIds: [id],
            }));

            bundles.push({
                id: `SEATS_${direction}`,
                products: seatProducts,
            });
        }

        if (bikesCount > 0) {
            const bikeProducts = passengerIds.slice(0, bikesCount).map((id, index) => ({
                ticketTypeCode: 'BIK',
                routeCode,
                sequenceNumber: passengerIds.size + index + 1,
                passengerIds: [id],
            }));

            bundles.push({
                id: `BIKES_${direction}`,
                products: bikeProducts,
            });
        }

        return bundles;
    };

    const buildJourney = ({ id, direction, service, fare }) => {
        const originStation = service.getIn(['origin', 'station']);
        const destinationStation = service.getIn(['destination', 'station']);
        const passengerIds = passengers.map((_, i) => `passenger_${i + 1}`);

        const ticketTypeCode = fare && fare.getIn(['ticketType', 'code']) || '';

        const routeCode = fare && fare.getIn(['route', 'code']) || fare.route.code;

        return {
            id: id,
            direction: direction,
            journeyConditions: {
                journeyOD: {
                    journeyOrigin: originStation ? originStation.get('nlc') || '' : '',
                    journeyDestination: destinationStation ? destinationStation.get('nlc') || '' : '',
                },
            },
            legs: buildLegs(service, direction),
            bundles: buildBundles({
                direction,
                ticketTypeCode,
                routeCode,
                passengerIds,
                bikesCount,
                requestSeats,
            }),
        };
    };

    const journeys = [];
    const formattedPassengers = [];

    for (let i = 0; i < passengers.size; i++) {
        const p = passengers.get(i);
        formattedPassengers.push({
            id: `passenger_${i + 1}`,
            statusCode: p && p.statusCode ? p.statusCode : '000',
        });
    }

    if (outwardService && outwardFare) {
        journeys.push(buildJourney({
            id: 'journey_1',
            direction: 'OUTBOUND',
            service: outwardService,
            fare: outwardFare,
        }));
    }

    if (inwardService && inwardFare) {
        journeys.push(buildJourney({
            id: 'journey_2',
            direction: 'INBOUND',
            service: inwardService,
            fare: inwardFare,
        }));
    }

    return {
        salesFlowType: 'SALES',
        passengers: formattedPassengers,
        journeys: journeys,
    };
};
        
const parseLegs = legs =>
    legs.map(({ origin, destination, serviceDetails }) => {
        const originFirst = origin.first();
        const destinationFirst = destination.first();

        return {
            date: trainTime(originFirst.time.scheduledTime).format('YYYY-MM-DD'),
            retailServiceId: serviceDetails.retailServiceId,
            origin: {
                name: originFirst.station.name,
                nlc: originFirst.station.nlc,
            },
            destination: {
                name: destinationFirst.station.name,
                nlc: destinationFirst.station.nlc,
            },
            departure: originFirst.time.scheduledTime,
            arrival: destinationFirst.time.scheduledTime,
            reservable: serviceDetails.reservable,
            operator: serviceDetails.toc.code,
            mode: serviceDetails.mode,
        };
    });

export const getReservationRequestJourney = (fare, journey) => {
    const { station: originStation } = journey.origin;
    const { station: destinationStation } = journey.destination;

    return {
        origin: {
            name: originStation.name,
            nlc: originStation.nlc,
        },
        destination: {
            name: destinationStation.name,
            nlc: destinationStation.nlc,
        },
        ticketType: fare && fare.ticketType && fare.ticketType.code,
        routeCode: fare && fare.route && fare.route.code,
        reserve: false,
        legs: parseLegs(journey.legs),
    };
};

export const sortReservations = (reservations) => 
    reservations.toArray().sort((a, b) => moment(b.created).diff(moment(a.created)));



// WEBPACK FOOTER //
// ./src/utils/reservationUtils.js